/**
 * ReportTabs Component - Tabs for different report types (Gesamt, Ablauf, Kurs)
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Search, X, BookOpen, ChevronDown } from 'lucide-react';
import { colors } from './constants';
import { UserCard } from './index';

// Number of items to show initially and per "load more" click
const INITIAL_ITEMS = 50;
const LOAD_MORE_ITEMS = 50;

export const GesamtReportTabs = ({ 
  reportResult, 
  filteredLists, 
  userSearch, 
  setUserSearch, 
  onUserClick 
}) => {
  return (
    <Tabs defaultValue="not_completed" className="w-full">
      <CardHeader className="pb-2">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="not_completed" className="text-xs" data-testid="tab-not-completed">
            Offen ({reportResult.summary?.not_completed || 0})
          </TabsTrigger>
          <TabsTrigger value="expired" className="text-xs" data-testid="tab-expired">
            Abgelaufen ({reportResult.summary?.expired || 0})
          </TabsTrigger>
          <TabsTrigger value="completed" className="text-xs" data-testid="tab-completed">
            Gültig ({reportResult.summary?.completed || 0})
          </TabsTrigger>
        </TabsList>
        {/* Search Input */}
        <SearchInput value={userSearch} onChange={setUserSearch} />
      </CardHeader>
      <CardContent>
        <TabsContent value="not_completed" className="mt-0">
          <UserList 
            users={filteredLists.notCompleted} 
            status="not_completed" 
            emptyMessage="Alle Kurse abgeschlossen!"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
        <TabsContent value="expired" className="mt-0">
          <UserList 
            users={filteredLists.expired} 
            status="not_completed" 
            emptyMessage="Keine abgelaufenen Zertifikate"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
        <TabsContent value="completed" className="mt-0">
          <UserList 
            users={filteredLists.completed} 
            status="completed" 
            emptyMessage="Noch keine gültigen Zertifikate"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
      </CardContent>
    </Tabs>
  );
};

export const AblaufReportTabs = ({ reportResult, userSearch, setUserSearch, onUserClick, activeExpiryFilter, setActiveExpiryFilter }) => {
  const [subFilter, setSubFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('expired');
  
  // Get the list based on main tab and sub-filter
  const getFilteredList = (mainTab) => {
    if (mainTab === 'expired') {
      if (subFilter === 'all') {
        return [
          ...(reportResult.expired_level_lists?.A1 || []),
          ...(reportResult.expired_level_lists?.A2 || []),
          ...(reportResult.expired_level_lists?.A3 || [])
        ];
      }
      return reportResult.expired_level_lists?.[subFilter] || [];
    }
    if (mainTab === 'expiring') {
      if (subFilter === 'all') {
        return [
          ...(reportResult.expiring_soon_lists?.L1 || []),
          ...(reportResult.expiring_soon_lists?.L2 || []),
          ...(reportResult.expiring_soon_lists?.L3 || [])
        ];
      }
      return reportResult.expiring_soon_lists?.[subFilter] || [];
    }
    if (mainTab === 'valid') return reportResult.valid_list || [];
    if (mainTab === 'open') return reportResult.open_list || [];
    if (mainTab === 'no_date') return reportResult.no_expiry_list || [];
    return [];
  };

  // Filter by search term
  const filterBySearch = (list) => {
    if (!userSearch) return list;
    const search = userSearch.toLowerCase();
    return list.filter(u => 
      (u.user_name || '').toLowerCase().includes(search) ||
      (u.user_email || '').toLowerCase().includes(search) ||
      (u.course_name || '').toLowerCase().includes(search) ||
      (u.teams || []).some(t => t.toLowerCase().includes(search))
    );
  };
  
  // Reset sub-filter when main tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSubFilter('all');
    // Update the chart filter based on tab
    if (tab === 'expired' && setActiveExpiryFilter) {
      setActiveExpiryFilter('A1');
    } else if (tab === 'expiring' && setActiveExpiryFilter) {
      setActiveExpiryFilter('L1');
    } else if (setActiveExpiryFilter) {
      setActiveExpiryFilter(null);
    }
  };

  const expiredLevels = reportResult.summary?.expired_levels || {};
  const expiringLevels = reportResult.summary?.expiring_soon_levels || {};

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <CardHeader>
        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Suche nach Name, E-Mail, Kurs oder Team..."
            className="w-full pl-10 pr-10 py-2 border rounded-md text-sm"
            style={{ borderColor: colors.border }}
            value={userSearch || ''}
            onChange={(e) => setUserSearch && setUserSearch(e.target.value)}
            data-testid="ablauf-search-input"
          />
          {userSearch && (
            <button
              onClick={() => setUserSearch && setUserSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>

        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="expired" className="text-xs" data-testid="ablauf-tab-expired">
            Abgelaufen ({reportResult.summary?.expired || 0})
          </TabsTrigger>
          <TabsTrigger value="expiring" className="text-xs" data-testid="ablauf-tab-expiring">
            Bald ({reportResult.summary?.expiring_soon || 0})
          </TabsTrigger>
          <TabsTrigger value="open" className="text-xs" data-testid="ablauf-tab-open">
            Offen ({reportResult.summary?.open || 0})
          </TabsTrigger>
          <TabsTrigger value="valid" className="text-xs" data-testid="ablauf-tab-valid">
            Gültig ({reportResult.summary?.valid || 0})
          </TabsTrigger>
          <TabsTrigger value="no_date" className="text-xs" data-testid="ablauf-tab-nodate">
            Ohne ({reportResult.summary?.no_expiry_date || 0})
          </TabsTrigger>
        </TabsList>
      </CardHeader>
      <CardContent>
        {/* Sub-filter buttons for Expired tab */}
        {activeTab === 'expired' && (
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button
              variant={subFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('all'); setActiveExpiryFilter && setActiveExpiryFilter('A1'); }}
              data-testid="ablauf-subfilter-all-expired"
            >
              Alle ({reportResult.summary?.expired || 0})
            </Button>
            <Button
              variant={subFilter === 'A1' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('A1'); setActiveExpiryFilter && setActiveExpiryFilter('A1'); }}
              className={subFilter === 'A1' ? 'bg-red-400 hover:bg-red-500' : ''}
              data-testid="ablauf-subfilter-a1"
            >
              A1 - bis 2 Wo. ({expiredLevels.A1 || 0})
            </Button>
            <Button
              variant={subFilter === 'A2' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('A2'); setActiveExpiryFilter && setActiveExpiryFilter('A2'); }}
              className={subFilter === 'A2' ? 'bg-red-500 hover:bg-red-600' : ''}
              data-testid="ablauf-subfilter-a2"
            >
              A2 - 2-4 Wo. ({expiredLevels.A2 || 0})
            </Button>
            <Button
              variant={subFilter === 'A3' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('A3'); setActiveExpiryFilter && setActiveExpiryFilter('A3'); }}
              className={subFilter === 'A3' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="ablauf-subfilter-a3"
            >
              A3 - mehr als 4 Wo. ({expiredLevels.A3 || 0})
            </Button>
          </div>
        )}

        {/* Sub-filter buttons for Expiring Soon tab */}
        {activeTab === 'expiring' && (
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button
              variant={subFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('all'); setActiveExpiryFilter && setActiveExpiryFilter('L1'); }}
              data-testid="ablauf-subfilter-all-expiring"
            >
              Alle ({reportResult.summary?.expiring_soon || 0})
            </Button>
            <Button
              variant={subFilter === 'L1' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('L1'); setActiveExpiryFilter && setActiveExpiryFilter('L1'); }}
              className={subFilter === 'L1' ? 'bg-yellow-400 hover:bg-yellow-500 text-black' : ''}
              data-testid="ablauf-subfilter-l1"
            >
              L1 - unter 4 Wo. ({expiringLevels.L1 || 0})
            </Button>
            <Button
              variant={subFilter === 'L2' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('L2'); setActiveExpiryFilter && setActiveExpiryFilter('L2'); }}
              className={subFilter === 'L2' ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : ''}
              data-testid="ablauf-subfilter-l2"
            >
              L2 - 4-6 Wo. ({expiringLevels.L2 || 0})
            </Button>
            <Button
              variant={subFilter === 'L3' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSubFilter('L3'); setActiveExpiryFilter && setActiveExpiryFilter('L3'); }}
              className={subFilter === 'L3' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              data-testid="ablauf-subfilter-l3"
            >
              L3 - 6-8 Wo. ({expiringLevels.L3 || 0})
            </Button>
          </div>
        )}

        <TabsContent value="expired" className="mt-0">
          <UserList 
            users={filterBySearch(getFilteredList('expired'))} 
            status="not_completed" 
            emptyMessage="Keine abgelaufenen Einträge"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
        <TabsContent value="expiring" className="mt-0">
          <UserList 
            users={filterBySearch(getFilteredList('expiring'))} 
            status="not_completed" 
            emptyMessage="Keine bald ablaufenden Einträge"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
        <TabsContent value="open" className="mt-0">
          <UserList 
            users={filterBySearch(getFilteredList('open'))} 
            status="not_completed" 
            emptyMessage="Keine offenen Einträge"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
        <TabsContent value="valid" className="mt-0">
          <UserList 
            users={filterBySearch(reportResult.valid_list || [])} 
            status="completed" 
            emptyMessage="Keine gültigen Einträge"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
        <TabsContent value="no_date" className="mt-0">
          <UserList 
            users={filterBySearch(reportResult.no_expiry_list || [])} 
            status="completed" 
            emptyMessage="Keine Einträge ohne Ablaufdatum"
            searchTerm={userSearch}
            onUserClick={onUserClick}
          />
        </TabsContent>
      </CardContent>
    </Tabs>
  );
};

export const KursReportSection = ({ courses, onUserClick }) => {
  return (
    <>
      <CardHeader>
        <CardTitle style={{ color: colors.dark }}>
          Kurse ({courses?.length || 0})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <div className="space-y-3">
            {courses?.map((course, idx) => (
              <CourseCollapsible key={idx} course={course} onUserClick={onUserClick} />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </>
  );
};

const SearchInput = ({ value, onChange }) => (
  <div className="relative mt-3">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
    <input
      type="text"
      placeholder="Benutzer suchen (Name oder E-Mail)..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
      style={{ borderColor: colors.border, color: colors.dark }}
      data-testid="user-search-input"
    />
    {value && (
      <button 
        onClick={() => onChange('')}
        className="absolute right-3 top-1/2 -translate-y-1/2"
      >
        <X className="h-4 w-4" style={{ color: colors.gray }} />
      </button>
    )}
  </div>
);

const UserList = ({ users, status, emptyMessage = '', searchTerm = '', onUserClick }) => {
  const [displayCount, setDisplayCount] = useState(INITIAL_ITEMS);
  
  // Reset display count when users or search changes
  const displayedUsers = useMemo(() => {
    // Reset to initial count when list changes significantly
    return users?.slice(0, displayCount) || [];
  }, [users, displayCount]);
  
  const hasMore = users && users.length > displayCount;
  const remainingCount = users ? users.length - displayCount : 0;
  
  const handleLoadMore = useCallback(() => {
    setDisplayCount(prev => prev + LOAD_MORE_ITEMS);
  }, []);
  
  // Reset display count when search term changes
  React.useEffect(() => {
    setDisplayCount(INITIAL_ITEMS);
  }, [searchTerm]);

  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-2">
        {displayedUsers.length > 0 ? (
          <>
            {displayedUsers.map((user, idx) => (
              <UserCard key={idx} user={user} status={status} onUserClick={onUserClick} />
            ))}
            {hasMore && (
              <div className="pt-2 pb-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleLoadMore}
                  data-testid="load-more-btn"
                >
                  <ChevronDown className="mr-2 h-4 w-4" />
                  {remainingCount} weitere laden
                </Button>
              </div>
            )}
          </>
        ) : searchTerm ? (
          <p className="text-center py-8" style={{ color: colors.gray }}>
            Keine Treffer für "{searchTerm}"
          </p>
        ) : (
          <p className="text-center py-8" style={{ color: colors.gray }}>
            {emptyMessage}
          </p>
        )}
      </div>
    </ScrollArea>
  );
};

const CourseCollapsible = ({ course, onUserClick }) => (
  <Collapsible>
    <div className="rounded-lg border bg-white" style={{ borderColor: colors.border }}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5" style={{ color: colors.blue }} />
            <div>
              <p className="font-semibold" style={{ color: colors.dark }}>{course.course}</p>
              <p className="text-sm" style={{ color: colors.gray }}>{course.total} Teilnehmer</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-green-600">{course.completed} ✓</p>
              <p className="text-sm font-medium text-orange-600">{course.not_completed} ✗</p>
            </div>
            <Badge style={{ 
              background: course.completion_rate >= 80 ? '#22c55e' : 
                         course.completion_rate >= 50 ? '#f59e0b' : '#ef4444' 
            }}>
              {course.completion_rate}%
            </Badge>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 border-t" style={{ borderColor: colors.border }}>
          {course.users_not_completed?.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium mb-2 text-orange-600">
                Nicht abgeschlossen ({course.users_not_completed.length})
              </p>
              <div className="space-y-1">
                {course.users_not_completed.slice(0, 10).map((user, i) => (
                  <UserCard key={i} user={user} status="not_completed" onUserClick={onUserClick} />
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </div>
  </Collapsible>
);
